// src/pages/Dashboard.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "../components/PageHeader";
import { abrirRelatorio } from "pages/utils/abrirRelatorio";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { useTheme } from "@mui/material/styles";
import Draggable from "react-draggable";
import useMediaQuery from "@mui/material/useMediaQuery";

import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Paper from "@mui/material/Paper";

import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import DashboardLazyAccordion from "pages/components/DashboardLazyAccordion";
import MateriaisRiscoDashboard from "pages/components/MateriaisRiscoDashboard";
import ObjetivosEstadoDashboard from "pages/components/ObjetivosEstadoDashboard";
import ObjetivosV2EstadoDashboard from "pages/components/ObjetivosV2EstadoDashboard";
import IncentivosVendedorDashboard from "pages/components/IncentivosVendedorDashboard";
import VendasMensaisVendedoresDashboard from "pages/components/VendasMensaisVendedoresDashboard";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";

// ========== Config ==========
const FILTERS_KEY = "tbl:dashboard:saldosvendedores:filters";
const KEEP_KEY = "tbl:dashboard:saldosvendedores:keep";
const ENDPOINT = "Dashboard/SaldosVendedores";
const KPIS_MESES_KEY = "dashboard:kpis:meses";
const KPIS_TOP_KEY = "dashboard:kpis:top";
const KPIS_TOP_ANO_KEY = "dashboard:kpis:topAno";

const MemoizedDefaultLineChart = React.memo(DefaultLineChart);
const MemoizedProjectTable = React.memo(ProjectTable);

function PaperComponent(props) {
  return (
    <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

function FaturaPaperComponent(props) {
  return (
    <Draggable handle="#fatura-detalhe-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

// ========== Filtro simples (Playbook) ==========
function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
      onFocus={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.35)")}
      onBlur={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.2)")}
    />
  );
}

// ========== Helpers ==========
const fmt = new Intl.NumberFormat("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (v) => (v == null || isNaN(v) ? 0 : Number(v));

const getMaxMesesFechadosAno = () => {
  const mesAtual = new Date().getMonth() + 1; // 1-12
  return mesAtual === 1 ? 12 : mesAtual - 1;
};

const normalizarMesesFechadosAno = (valor) => {
  const max = getMaxMesesFechadosAno();
  const n = parseInt(valor, 10);

  if (Number.isNaN(n)) return 1;
  if (n < 1) return 1;
  if (n > max) return max;

  return n;
};

const normalizarKpisMeses = (valor) => {
  const n = parseInt(valor, 10);

  if (Number.isNaN(n)) return 12;
  if (n < 1) return 1;
  if (n > 24) n = 24;

  return n;
};

const getAnosAnaliseMesesFechadosAno = () => {
  const hoje = new Date();
  const mesAtual = hoje.getMonth() + 1;

  if (mesAtual === 1) {
    return {
      anoAtualAnalise: hoje.getFullYear() - 1,
      anoAnteriorAnalise: hoje.getFullYear() - 2,
    };
  }

  return {
    anoAtualAnalise: hoje.getFullYear(),
    anoAnteriorAnalise: hoje.getFullYear() - 1,
  };
};

const getMesLabel = (mes) => {
  const meses = [
    "Jan",
    "Fev",
    "Mar",
    "Abr",
    "Mai",
    "Jun",
    "Jul",
    "Ago",
    "Set",
    "Out",
    "Nov",
    "Dez",
  ];

  if (!mes || mes < 1) return "";
  if (mes > 12) return "Dez";

  return meses[mes - 1];
};

// =================== Componente ===================
export default function Dashboard() {
  const { showAlert } = useAlert();
  const { userData, temFuncao } = useAuth();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const ultimoRefreshAutoRef = useRef(0);

  const [kpi, setKpi] = useState({ sum_91a120: 0, sum_121a150: 0, sum_151mais: 0 });

  const [topDialogOpen, setTopDialogOpen] = useState(false);
  const [topDialogTitle, setTopDialogTitle] = useState("");
  const [topDialogRows, setTopDialogRows] = useState([]);
  const [topDialogLoading, setTopDialogLoading] = useState(false);

  const [topDialogHeaders, setTopDialogHeaders] = useState([
    "#",
    "Nome",
    "Total € (12m)",
    "Total € (12m ant.)",
    "Dif €",
    "Dif %",
  ]);

  const [topParceiros, setTopParceiros] = useState([]);
  const [topParceirosTotal, setTopParceirosTotal] = useState(0);
  const [topClientes, setTopClientes] = useState([]);
  const [topClientesTotal, setTopClientesTotal] = useState(0);
  const [topSubiram, setTopSubiram] = useState([]);
  const [topSubiramTotal, setTopSubiramTotal] = useState(0);
  const [topDesceram, setTopDesceram] = useState([]);
  const [topDesceramTotal, setTopDesceramTotal] = useState(0);

  const [loadingKpis12Meses, setLoadingKpis12Meses] = useState(false);
  const [kpis12MesesCarregados, setKpis12MesesCarregados] = useState(false);

  const [mesesKpis, setMesesKpis] = useState(() => {
    try {
      const guardado = localStorage.getItem(KPIS_MESES_KEY);

      if (!guardado) return 12;

      return normalizarKpisMeses(guardado);
    } catch {
      return 12;
    }
  });

  const [mesesFechadosAno, setMesesFechadosAno] = useState(() => getMaxMesesFechadosAno());

  const [topKpis, setTopKpis] = useState(() => {
    try {
      const guardado = Number(localStorage.getItem(KPIS_TOP_KEY));

      return [10, 20, 30, 40, 50].includes(guardado) ? guardado : 20;
    } catch {
      return 20;
    }
  });

  const [topKpisAno, setTopKpisAno] = useState(() => {
    try {
      const guardado = Number(localStorage.getItem(KPIS_TOP_ANO_KEY));

      return [10, 20, 30, 40, 50].includes(guardado) ? guardado : 20;
    } catch {
      return 20;
    }
  });

  const [topParceirosAno, setTopParceirosAno] = useState([]);
  const [topParceirosAnoTotal, setTopParceirosAnoTotal] = useState(0);
  const [topClientesAno, setTopClientesAno] = useState([]);
  const [topClientesAnoTotal, setTopClientesAnoTotal] = useState(0);
  const [topSubiramAno, setTopSubiramAno] = useState([]);
  const [topSubiramAnoTotal, setTopSubiramAnoTotal] = useState(0);
  const [topDesceramAno, setTopDesceramAno] = useState([]);
  const [topDesceramAnoTotal, setTopDesceramAnoTotal] = useState(0);

  const [loadingKpisAno, setLoadingKpisAno] = useState(false);
  const [kpisAnoCarregados, setKpisAnoCarregados] = useState(false);

  const [incentivosRefreshToken, setIncentivosRefreshToken] = useState(0);
  const [loadingIncentivos, setLoadingIncentivos] = useState(false);
  const [incentivosCarregados, setIncentivosCarregados] = useState(false);
  const [incentivosVazios, setIncentivosVazios] = useState(false);

  const [objetivosRefreshToken, setObjetivosRefreshToken] = useState(0);
  const [loadingObjetivos, setLoadingObjetivos] = useState(false);
  const [objetivosCarregados, setObjetivosCarregados] = useState(false);

  const [objetivosV2RefreshToken, setObjetivosV2RefreshToken] = useState(0);
  const [loadingObjetivosV2, setLoadingObjetivosV2] = useState(false);
  const [objetivosV2Carregados, setObjetivosV2Carregados] = useState(false);

  const [materiaisRiscoRefreshToken, setMateriaisRiscoRefreshToken] = useState(0);
  const [loadingMateriaisRisco, setLoadingMateriaisRisco] = useState(false);
  const [materiaisRiscoCarregados, setMateriaisRiscoCarregados] = useState(false);
  const [materiaisRiscoVazios, setMateriaisRiscoVazios] = useState(false);

  const [vendasVendedoresRefreshToken, setVendasVendedoresRefreshToken] = useState(0);
  const [loadingVendasVendedores, setLoadingVendasVendedores] = useState(false);
  const [vendasVendedoresCarregadas, setVendasVendedoresCarregadas] = useState(false);
  const [vendasVendedoresVazias, setVendasVendedoresVazias] = useState(false);

  const [loadingDebitosSaldos, setLoadingDebitosSaldos] = useState(false);
  const [debitosSaldosCarregados, setDebitosSaldosCarregados] = useState(false);
  const [loadingMais90Pdf, setLoadingMais90Pdf] = useState(false);
  const [loadingRelatorioSaldo, setLoadingRelatorioSaldo] = useState("");

  const [vendedoresDebitosSaldos, setVendedoresDebitosSaldos] = useState([]);
  const [refVendedorDebitosSaldos, setRefVendedorDebitosSaldos] = useState("");

  async function carregarTopDesceramKpi() {
    const podeVerGeral =
      temFuncao("Administração") ||
      temFuncao("Informática") ||
      temFuncao("Tesouraria") ||
      temFuncao("Gestão Objetivos");

    const body = podeVerGeral
      ? {
          meses: mesesKpis,
          top: topKpis,
        }
      : {
          refvendedor: userData?.refvendedor || null,
          meses: mesesKpis,
          top: topKpis,
        };

    const [data, err] = await handleApi(api.post("Dashboard/SelClientesMaisDesceram12Meses", body));
    if (err || !Array.isArray(data)) return;

    setTopDesceram(data.slice(0, 3));

    // Aqui faz sentido mostrar a "perda" total (soma das difs, valor negativo)
    const totalDif20 = data.reduce((sum, r) => sum + Number(r?.dif || 0), 0);
    setTopDesceramTotal(totalDif20);
  }

  async function carregarTopSubiramKpi() {
    const podeVerGeral =
      temFuncao("Administração") ||
      temFuncao("Informática") ||
      temFuncao("Tesouraria") ||
      temFuncao("Gestão Objetivos");

    const body = podeVerGeral
      ? {
          meses: mesesKpis,
          top: topKpis,
        }
      : {
          refvendedor: userData?.refvendedor || null,
          meses: mesesKpis,
          top: topKpis,
        };

    const [data, err] = await handleApi(api.post("Dashboard/SelClientesMaisSubiram12Meses", body));
    if (err || !Array.isArray(data)) return;

    setTopSubiram(data.slice(0, 3));

    // Para este KPI, o valor principal faz mais sentido ser a soma das DIFs (ganho)
    const totalDif20 = data.reduce((sum, r) => sum + Number(r?.dif || 0), 0);
    setTopSubiramTotal(totalDif20);
  }

  async function carregarTopParceirosKpi() {
    const podeVerGeral =
      temFuncao("Administração") ||
      temFuncao("Informática") ||
      temFuncao("Tesouraria") ||
      temFuncao("Gestão Objetivos");

    const body = podeVerGeral
      ? {
          meses: mesesKpis,
          top: topKpis,
        }
      : {
          refvendedor: userData?.refvendedor || null,
          meses: mesesKpis,
          top: topKpis,
        };

    const [data, err] = await handleApi(api.post("Dashboard/SelTopParceiros12Meses", body));

    if (err || !Array.isArray(data)) return;

    // Top 3 para o pódio
    setTopParceiros(data.slice(0, 3));

    // Total agregado do Top 20 (período atual)
    const total20 = data.reduce((sum, r) => sum + Number(r?.total_atual || 0), 0);

    setTopParceirosTotal(total20);
  }

  async function carregarTopClientesKpi() {
    const podeVerGeral =
      temFuncao("Administração") ||
      temFuncao("Informática") ||
      temFuncao("Tesouraria") ||
      temFuncao("Gestão Objetivos");

    const body = podeVerGeral
      ? {
          meses: mesesKpis,
          top: topKpis,
        }
      : {
          refvendedor: userData?.refvendedor || null,
          meses: mesesKpis,
          top: topKpis,
        };

    const [data, err] = await handleApi(api.post("Dashboard/SelTopClientes12Meses", body));

    if (err || !Array.isArray(data)) return;

    setTopClientes(data.slice(0, 3));

    const total20 = data.reduce((sum, r) => sum + Number(r?.total_atual || 0), 0);
    setTopClientesTotal(total20);
  }

  async function carregarTopParceirosAnoKpi() {
    const podeVerGeral =
      temFuncao("Administração") ||
      temFuncao("Informática") ||
      temFuncao("Tesouraria") ||
      temFuncao("Gestão Objetivos");

    const body = podeVerGeral
      ? {
          meses: mesesFechadosAno,
          top: topKpisAno,
        }
      : {
          refvendedor: userData?.refvendedor || null,
          meses: mesesFechadosAno,
          top: topKpisAno,
        };

    const [data, err] = await handleApi(
      api.post("Dashboard/SelTopParceirosAnoMesesFechados", body)
    );

    if (err || !Array.isArray(data)) return;

    setTopParceirosAno(data.slice(0, 3));

    const total20 = data.reduce((sum, r) => sum + Number(r?.total_atual || 0), 0);
    setTopParceirosAnoTotal(total20);
  }

  async function carregarTopClientesAnoKpi() {
    const podeVerGeral =
      temFuncao("Administração") ||
      temFuncao("Informática") ||
      temFuncao("Tesouraria") ||
      temFuncao("Gestão Objetivos");

    const body = podeVerGeral
      ? {
          meses: mesesFechadosAno,
          top: topKpisAno,
        }
      : {
          refvendedor: userData?.refvendedor || null,
          meses: mesesFechadosAno,
          top: topKpisAno,
        };

    const [data, err] = await handleApi(api.post("Dashboard/SelTopClientesAnoMesesFechados", body));

    if (err || !Array.isArray(data)) return;

    setTopClientesAno(data.slice(0, 3));

    const total20 = data.reduce((sum, r) => sum + Number(r?.total_atual || 0), 0);
    setTopClientesAnoTotal(total20);
  }

  async function carregarTopSubiramAnoKpi() {
    const podeVerGeral =
      temFuncao("Administração") ||
      temFuncao("Informática") ||
      temFuncao("Tesouraria") ||
      temFuncao("Gestão Objetivos");

    const body = podeVerGeral
      ? {
          meses: mesesFechadosAno,
          top: topKpisAno,
        }
      : {
          refvendedor: userData?.refvendedor || null,
          meses: mesesFechadosAno,
          top: topKpisAno,
        };

    const [data, err] = await handleApi(
      api.post("Dashboard/SelClientesMaisSubiramAnoMesesFechados", body)
    );

    if (err || !Array.isArray(data)) return;

    setTopSubiramAno(data.slice(0, 3));

    const totalDif20 = data.reduce((sum, r) => sum + Number(r?.dif || 0), 0);
    setTopSubiramAnoTotal(totalDif20);
  }

  async function carregarTopDesceramAnoKpi() {
    const podeVerGeral =
      temFuncao("Administração") ||
      temFuncao("Informática") ||
      temFuncao("Tesouraria") ||
      temFuncao("Gestão Objetivos");

    const body = podeVerGeral
      ? {
          meses: mesesFechadosAno,
          top: topKpisAno,
        }
      : {
          refvendedor: userData?.refvendedor || null,
          meses: mesesFechadosAno,
          top: topKpisAno,
        };

    const [data, err] = await handleApi(
      api.post("Dashboard/SelClientesMaisDesceramAnoMesesFechados", body)
    );

    if (err || !Array.isArray(data)) return;

    setTopDesceramAno(data.slice(0, 3));

    const totalDif20 = data.reduce((sum, r) => sum + Number(r?.dif || 0), 0);
    setTopDesceramAnoTotal(totalDif20);
  }

  function atualizarIncentivos() {
    if (loadingIncentivos) return;

    setLoadingIncentivos(true);
    setIncentivosRefreshToken((prev) => prev + 1);
  }

  function atualizarObjetivos() {
    if (loadingObjetivos) return;

    setLoadingObjetivos(true);
    setObjetivosRefreshToken((prev) => prev + 1);
  }

  function atualizarObjetivosV2() {
    if (loadingObjetivosV2) return;

    setLoadingObjetivosV2(true);
    setObjetivosV2RefreshToken((prev) => prev + 1);
  }

  function atualizarMateriaisRisco() {
    if (loadingMateriaisRisco) return;

    setLoadingMateriaisRisco(true);
    setMateriaisRiscoRefreshToken((prev) => prev + 1);
  }

  function atualizarVendasVendedores() {
    if (loadingVendasVendedores) return;

    setLoadingVendasVendedores(true);
    setVendasVendedoresRefreshToken((prev) => prev + 1);
  }

  async function carregarVendedoresDebitosSaldos() {
    const idUtilizador = Number(userData?.id || 0);

    if (!idUtilizador) {
      setVendedoresDebitosSaldos([]);
      setRefVendedorDebitosSaldos("");
      return;
    }

    const [data, err] = await handleApi(
      api.post("Dashboard/SelVendedoresPermitidosDashboard", {
        idutilizador: idUtilizador,
      })
    );

    if (err || !Array.isArray(data)) {
      setVendedoresDebitosSaldos([]);
      setRefVendedorDebitosSaldos("");
      return;
    }

    setVendedoresDebitosSaldos(data);

    setRefVendedorDebitosSaldos("");
  }

  async function carregarKpiPorVendedor(refVendedor) {
    const idUtilizador = Number(userData?.id || 0);

    if (!idUtilizador || !refVendedor) {
      setKpi({
        sum_91a120: 0,
        sum_121a150: 0,
        sum_151mais: 0,
      });
      return;
    }

    const [data, err] = await handleApi(
      api.post("Dashboard/SumClientesEmAtrasoPorVendedor", {
        idutilizador: idUtilizador,
        refvendedor: refVendedor,
      })
    );

    if (err) {
      showAlert(err, "error");

      setKpi({
        sum_91a120: 0,
        sum_121a150: 0,
        sum_151mais: 0,
      });

      return;
    }

    setKpi({
      sum_91a120: Number(data?.sum_91a120 ?? 0),
      sum_121a150: Number(data?.sum_121a150 ?? 0),
      sum_151mais: Number(data?.sum_151mais ?? 0),
    });
  }

  async function carregarSaldosPorVendedor(refVendedor) {
    const idUtilizador = Number(userData?.id || 0);

    if (!idUtilizador || !refVendedor) {
      setRowsData([]);
      return;
    }

    const [data, err] = await handleApi(
      api.post("Dashboard/SaldosVendedoresPorRef", {
        idutilizador: idUtilizador,
        refvendedor: refVendedor,
      })
    );

    if (err) {
      setErro(err);
      showAlert(err, "error");
      setRowsData([]);
      return;
    }

    setRowsData(Array.isArray(data) ? data : []);
    setErro("");
  }

  async function atualizarDebitosSaldos() {
    if (loadingDebitosSaldos) return;

    setLoadingDebitosSaldos(true);

    try {
      const idUtilizador = Number(userData?.id || 0);

      if (!idUtilizador) {
        setDebitosSaldosCarregados(true);
        return;
      }

      let vendedores = vendedoresDebitosSaldos;

      if (!vendedores.length) {
        const [dataVendedores, errVendedores] = await handleApi(
          api.post("Dashboard/SelVendedoresPermitidosDashboard", {
            idutilizador: idUtilizador,
          })
        );

        if (errVendedores || !Array.isArray(dataVendedores)) {
          setVendedoresDebitosSaldos([]);
          setRowsData([]);
          setDebitosSaldosCarregados(true);
          return;
        }

        vendedores = dataVendedores
          .map((v) => ({
            ref: String(v?.ref ?? "").trim(),
            nome: String(v?.nome ?? "").trim(),
          }))
          .filter((v) => v.ref);

        setVendedoresDebitosSaldos(vendedores);
      }

      if (!refVendedorDebitosSaldos) {
        await Promise.all([carregarKpi(), carregar()]);
      } else {
        await Promise.all([
          carregarKpiPorVendedor(refVendedorDebitosSaldos),
          carregarSaldosPorVendedor(refVendedorDebitosSaldos),
        ]);
      }

      setDebitosSaldosCarregados(true);
    } finally {
      setLoadingDebitosSaldos(false);
    }
  }

  async function atualizarKpis12Meses() {
    if (loadingKpis12Meses) return;

    setLoadingKpis12Meses(true);

    try {
      await Promise.all([
        carregarTopParceirosKpi(),
        carregarTopClientesKpi(),
        carregarTopSubiramKpi(),
        carregarTopDesceramKpi(),
      ]);

      setKpis12MesesCarregados(true);
    } finally {
      setLoadingKpis12Meses(false);
    }
  }

  async function atualizarKpisAno() {
    if (loadingKpisAno) return;

    setLoadingKpisAno(true);

    try {
      await Promise.all([
        carregarTopParceirosAnoKpi(),
        carregarTopClientesAnoKpi(),
        carregarTopSubiramAnoKpi(),
        carregarTopDesceramAnoKpi(),
      ]);

      setKpisAnoCarregados(true);
    } finally {
      setLoadingKpisAno(false);
    }
  }

  async function abrirTop20Desceram() {
    try {
      const podeVerGeral =
        temFuncao("Administração") ||
        temFuncao("Informática") ||
        temFuncao("Tesouraria") ||
        temFuncao("Gestão Objetivos");

      const body = podeVerGeral
        ? {
            meses: mesesKpis,
            top: topKpis,
          }
        : {
            refvendedor: userData?.refvendedor || null,
            meses: mesesKpis,
            top: topKpis,
          };

      setTopDialogTitle(
        `Top ${topKpis} Clientes que mais desceram (${mesesKpis}m vs ${mesesKpis}m anteriores)`
      );

      setTopDialogHeaders([
        "#",
        "Nome",
        `Total € (${mesesKpis}m)`,
        `Total € (${mesesKpis}m ant.)`,
        "Dif €",
        "Dif %",
      ]);
      setTopDialogRows([]);
      setTopDialogOpen(true);
      setTopDialogLoading(true);

      const [data, err] = await handleApi(
        api.post("Dashboard/SelClientesMaisDesceram12Meses", body)
      );
      setTopDialogLoading(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      setTopDialogRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setTopDialogLoading(false);
      showAlert(e?.message || "Erro ao carregar Top 20 (Desceram).", "error");
    }
  }

  async function abrirTop20Subiram() {
    try {
      const podeVerGeral =
        temFuncao("Administração") ||
        temFuncao("Informática") ||
        temFuncao("Tesouraria") ||
        temFuncao("Gestão Objetivos");

      const body = podeVerGeral
        ? {
            meses: mesesKpis,
            top: topKpis,
          }
        : {
            refvendedor: userData?.refvendedor || null,
            meses: mesesKpis,
            top: topKpis,
          };

      setTopDialogTitle(
        `Top ${topKpis} Clientes que mais subiram (${mesesKpis}m vs ${mesesKpis}m anteriores)`
      );

      setTopDialogHeaders([
        "#",
        "Nome",
        `Total € (${mesesKpis}m)`,
        `Total € (${mesesKpis}m ant.)`,
        "Dif €",
        "Dif %",
      ]);
      setTopDialogRows([]);
      setTopDialogOpen(true);
      setTopDialogLoading(true);

      const [data, err] = await handleApi(
        api.post("Dashboard/SelClientesMaisSubiram12Meses", body)
      );
      setTopDialogLoading(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      setTopDialogRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setTopDialogLoading(false);
      showAlert(e?.message || "Erro ao carregar Top 20 (Subiram).", "error");
    }
  }

  async function abrirTop20Parceiros() {
    try {
      const podeVerGeral =
        temFuncao("Administração") ||
        temFuncao("Informática") ||
        temFuncao("Tesouraria") ||
        temFuncao("Gestão Objetivos");

      const body = podeVerGeral
        ? {
            meses: mesesKpis,
            top: topKpis,
          }
        : {
            refvendedor: userData?.refvendedor || null,
            meses: mesesKpis,
            top: topKpis,
          };

      setTopDialogTitle(`Top ${topKpis} Parceiros (${mesesKpis}m vs ${mesesKpis}m anteriores)`);

      setTopDialogHeaders([
        "#",
        "Nome",
        `Total € (${mesesKpis}m)`,
        `Total € (${mesesKpis}m ant.)`,
        "Dif €",
        "Dif %",
      ]);
      setTopDialogRows([]);
      setTopDialogOpen(true);
      setTopDialogLoading(true);

      const [data, err] = await handleApi(api.post("Dashboard/SelTopParceiros12Meses", body));
      setTopDialogLoading(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      setTopDialogRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setTopDialogLoading(false);
      showAlert(e?.message || "Erro ao carregar Top 20 Parceiros.", "error");
    }
  }

  async function abrirTop20Clientes() {
    try {
      const podeVerGeral =
        temFuncao("Administração") ||
        temFuncao("Informática") ||
        temFuncao("Tesouraria") ||
        temFuncao("Gestão Objetivos");

      const body = podeVerGeral
        ? {
            meses: mesesKpis,
            top: topKpis,
          }
        : {
            refvendedor: userData?.refvendedor || null,
            meses: mesesKpis,
            top: topKpis,
          };

      setTopDialogTitle(`Top ${topKpis} Clientes (${mesesKpis}m vs ${mesesKpis}m anteriores)`);

      setTopDialogHeaders([
        "#",
        "Nome",
        `Total € (${mesesKpis}m)`,
        `Total € (${mesesKpis}m ant.)`,
        "Dif €",
        "Dif %",
      ]);
      setTopDialogRows([]);
      setTopDialogOpen(true);
      setTopDialogLoading(true);

      const [data, err] = await handleApi(api.post("Dashboard/SelTopClientes12Meses", body));
      setTopDialogLoading(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      setTopDialogRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setTopDialogLoading(false);
      showAlert(e?.message || "Erro ao carregar Top 20 Clientes.", "error");
    }
  }

  async function abrirTop20ParceirosAno() {
    try {
      const podeVerGeral =
        temFuncao("Administração") ||
        temFuncao("Informática") ||
        temFuncao("Tesouraria") ||
        temFuncao("Gestão Objetivos");

      const body = podeVerGeral
        ? {
            meses: mesesFechadosAno,
            top: topKpisAno,
          }
        : {
            refvendedor: userData?.refvendedor || null,
            meses: mesesFechadosAno,
            top: topKpisAno,
          };

      setTopDialogTitle(
        `Top ${topKpisAno} Parceiros (${anoAtualAnalise} Jan-${mesLabel} vs ${anoAnteriorAnalise} Jan-${mesLabel})`
      );
      setTopDialogHeaders([
        "#",
        "Nome",
        `Total € (${anoAtualAnalise} Jan-${mesLabel})`,
        `Total € (${anoAnteriorAnalise} Jan-${mesLabel})`,
        "Dif €",
        "Dif %",
      ]);
      setTopDialogRows([]);
      setTopDialogOpen(true);
      setTopDialogLoading(true);

      const [data, err] = await handleApi(
        api.post("Dashboard/SelTopParceirosAnoMesesFechados", body)
      );
      setTopDialogLoading(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      setTopDialogRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setTopDialogLoading(false);
      showAlert(e?.message || "Erro ao carregar Top 20 Parceiros.", "error");
    }
  }

  async function abrirTop20ClientesAno() {
    try {
      const podeVerGeral =
        temFuncao("Administração") ||
        temFuncao("Informática") ||
        temFuncao("Tesouraria") ||
        temFuncao("Gestão Objetivos");

      const body = podeVerGeral
        ? {
            meses: mesesFechadosAno,
            top: topKpisAno,
          }
        : {
            refvendedor: userData?.refvendedor || null,
            meses: mesesFechadosAno,
            top: topKpisAno,
          };

      setTopDialogTitle(
        `Top ${topKpisAno} Clientes (${anoAtualAnalise} Jan-${mesLabel} vs ${anoAnteriorAnalise} Jan-${mesLabel})`
      );
      setTopDialogHeaders([
        "#",
        "Nome",
        `Total € (${anoAtualAnalise} Jan-${mesLabel})`,
        `Total € (${anoAnteriorAnalise} Jan-${mesLabel})`,
        "Dif €",
        "Dif %",
      ]);
      setTopDialogRows([]);
      setTopDialogOpen(true);
      setTopDialogLoading(true);

      const [data, err] = await handleApi(
        api.post("Dashboard/SelTopClientesAnoMesesFechados", body)
      );
      setTopDialogLoading(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      setTopDialogRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setTopDialogLoading(false);
      showAlert(e?.message || "Erro ao carregar Top 20 Clientes.", "error");
    }
  }

  async function abrirTop20SubiramAno() {
    try {
      const podeVerGeral =
        temFuncao("Administração") ||
        temFuncao("Informática") ||
        temFuncao("Tesouraria") ||
        temFuncao("Gestão Objetivos");

      const body = podeVerGeral
        ? {
            meses: mesesFechadosAno,
            top: topKpisAno,
          }
        : {
            refvendedor: userData?.refvendedor || null,
            meses: mesesFechadosAno,
            top: topKpisAno,
          };

      setTopDialogTitle(
        `Top ${topKpisAno} Clientes que mais subiram (${anoAtualAnalise} Jan-${mesLabel} vs ${anoAnteriorAnalise} Jan-${mesLabel})`
      );
      setTopDialogHeaders([
        "#",
        "Nome",
        `Total € (${anoAtualAnalise} Jan-${mesLabel})`,
        `Total € (${anoAnteriorAnalise} Jan-${mesLabel})`,
        "Dif €",
        "Dif %",
      ]);
      setTopDialogRows([]);
      setTopDialogOpen(true);
      setTopDialogLoading(true);

      const [data, err] = await handleApi(
        api.post("Dashboard/SelClientesMaisSubiramAnoMesesFechados", body)
      );
      setTopDialogLoading(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      setTopDialogRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setTopDialogLoading(false);
      showAlert(e?.message || "Erro ao carregar Top 20 (Subiram).", "error");
    }
  }

  async function abrirTop20DesceramAno() {
    try {
      const podeVerGeral =
        temFuncao("Administração") ||
        temFuncao("Informática") ||
        temFuncao("Tesouraria") ||
        temFuncao("Gestão Objetivos");

      const body = podeVerGeral
        ? {
            meses: mesesFechadosAno,
            top: topKpisAno,
          }
        : {
            refvendedor: userData?.refvendedor || null,
            meses: mesesFechadosAno,
            top: topKpisAno,
          };

      setTopDialogTitle(
        `Top ${topKpisAno} Clientes que mais desceram (${anoAtualAnalise} Jan-${mesLabel} vs ${anoAnteriorAnalise} Jan-${mesLabel})`
      );
      setTopDialogHeaders([
        "#",
        "Nome",
        `Total € (${anoAtualAnalise} Jan-${mesLabel})`,
        `Total € (${anoAnteriorAnalise} Jan-${mesLabel})`,
        "Dif €",
        "Dif %",
      ]);
      setTopDialogRows([]);
      setTopDialogOpen(true);
      setTopDialogLoading(true);

      const [data, err] = await handleApi(
        api.post("Dashboard/SelClientesMaisDesceramAnoMesesFechados", body)
      );
      setTopDialogLoading(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      setTopDialogRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setTopDialogLoading(false);
      showAlert(e?.message || "Erro ao carregar Top 20 (Desceram).", "error");
    }
  }

  // Perfis
  const isAdminLike = useMemo(
    () => temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria"),
    [temFuncao]
  );
  const isComercial = useMemo(() => temFuncao("Comercial"), [temFuncao]);
  const podeVerTabela = isAdminLike || isComercial;

  // ref do vendedor autenticado
  const refUser = useMemo(() => userData?.refvendedor?.toString?.() || "", [userData]);
  const podeVerKpis = isAdminLike || (isComercial && !!refUser);

  const podeVerRuturas = isAdminLike;

  // ---------- Estado da tabela ----------
  const [loading, setLoading] = useState(true);
  const [rowsData, setRowsData] = useState([]);
  const [erro, setErro] = useState("");

  // === vendas (para o gráfico) ===
  const [vendas3Anos, setVendas3Anos] = useState([]);
  const [loadingVendas, setLoadingVendas] = useState(false);

  // === detalhe mensal das vendas ===
  const [vendasDetalheOpen, setVendasDetalheOpen] = useState(false);
  const [vendasDetalheLoading, setVendasDetalheLoading] = useState(false);
  const [vendasDetalheRows, setVendasDetalheRows] = useState([]);
  const [vendasDetalheAno, setVendasDetalheAno] = useState(null);
  const [vendasDetalheMes, setVendasDetalheMes] = useState(null);
  const [vendasDetalheTotalGrafico, setVendasDetalheTotalGrafico] = useState(0);
  const [vendasDetalheLimite, setVendasDetalheLimite] = useState(100);

  // === comparativo de vendas até ao último dia apurado ===
  const [vendasComparativoOpen, setVendasComparativoOpen] = useState(false);
  const [vendasComparativoLoading, setVendasComparativoLoading] = useState(false);
  const [vendasComparativoRows, setVendasComparativoRows] = useState([]);
  const [vendasComparativoDataApurada, setVendasComparativoDataApurada] = useState(null);
  const [vendasComparativoAtualizadoEm, setVendasComparativoAtualizadoEm] = useState(null);

  // === detalhe de uma fatura ===
  const [faturaDetalheOpen, setFaturaDetalheOpen] = useState(false);
  const [faturaDetalheLoading, setFaturaDetalheLoading] = useState(false);
  const [faturaDetalheRows, setFaturaDetalheRows] = useState([]);
  const [faturaDetalheSelecionada, setFaturaDetalheSelecionada] = useState(null);

  // === última sincronização de faturas de vendas ===
  const [ultimaSyncFaturasVendas, setUltimaSyncFaturasVendas] = useState(null);

  // persistência "Manter filtros"
  const initKeep = (() => {
    try {
      const v = localStorage.getItem(KEEP_KEY);
      return v === null ? true : JSON.parse(v);
    } catch {
      return true;
    }
  })();

  const [keepFilters, setKeepFilters] = useState(initKeep);

  const initialFilters = keepFilters
    ? (() => {
        try {
          return JSON.parse(localStorage.getItem(FILTERS_KEY) || "[]");
        } catch {
          return [];
        }
      })()
    : [];

  const { anoAtualAnalise, anoAnteriorAnalise } = getAnosAnaliseMesesFechadosAno();

  const mesLabel = getMesLabel(mesesFechadosAno);

  const colorMap = {
    warning: theme.palette.warning.main,
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
  };

  const vendasChart = useMemo(() => {
    if (!Array.isArray(vendas3Anos) || vendas3Anos.length === 0) return null;

    const anoAtual = new Date().getFullYear();
    const anos = [anoAtual, anoAtual - 1, anoAtual - 2];

    const labelsMeses = [
      "Jan",
      "Fev",
      "Mar",
      "Abr",
      "Mai",
      "Jun",
      "Jul",
      "Ago",
      "Set",
      "Out",
      "Nov",
      "Dez",
    ];

    const porAno = {};
    const totaisAno = {};
    anos.forEach((a) => {
      porAno[a] = new Array(12).fill(0);
      totaisAno[a] = 0;
    });

    for (const r of vendas3Anos) {
      const ano = Number(r.ano);
      const mes = Number(r.mes); // 1-12
      const valor = Number(r.total ?? 0);

      if (!anos.includes(ano)) continue;
      const idxMes = mes - 1;
      if (idxMes < 0 || idxMes > 11) continue;

      porAno[ano][idxMes] += valor;
      totaisAno[ano] += valor;
    }

    // Esconder meses FUTUROS do ano atual (como no MenuSearch)
    const mesAtual = new Date().getMonth(); // 0-11

    for (let m = mesAtual + 1; m < 12; m += 1) {
      if (porAno[anoAtual]) {
        porAno[anoAtual][m] = null;
      }
    }

    const tipo = isAdminLike ? "Geral" : "Comercial";
    const cores = ["warning", "primary", "secondary"];

    const datasets = anos.map((ano, idx) => ({
      // label: `${ano} – ${tipo} (${fmt.format(totaisAno[ano])} €)`,
      label: `${ano}`,
      data: porAno[ano],
      tension: 0.4,
      pointRadius: 3,
      borderWidth: 3,
      color: cores[idx] || "info",
    }));

    return {
      labels: labelsMeses,
      datasets,
      totaisAno,
      tipo,
    };
  }, [vendas3Anos, isAdminLike]);

  const vendasDetalheColumns = useMemo(
    () => [
      {
        Header: "Data",
        accessor: "faturadata",
        width: 105,
        minWidth: 95,
        sortable: false,
        Cell: ({ value }) =>
          value
            ? new Date(value).toLocaleDateString("pt-PT", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : "",
      },
      {
        Header: "Fatura",
        accessor: "faturanumero",
        width: 140,
        sortable: false,
      },
      {
        Header: "Cliente",
        accessor: "cliente",
        width: 300,
        sortable: false,
      },
      {
        Header: "Vendedor",
        accessor: "vendedor",
        width: 220,
        sortable: false,
      },
      {
        Header: "Linhas",
        accessor: "linhas",
        width: 85,
        align: "right",
        sortable: false,
        Cell: ({ value }) => Number(value || 0),
      },
      {
        Header: "Total fatura €",
        accessor: "valorfatura",
        width: 135,
        align: "right",
        sortable: false,
        Cell: ({ value }) => fmt.format(num(value)),
      },
      {
        Header: "Acumulado €",
        accessor: "acumulado",
        width: 140,
        align: "right",
        sortable: false,
        Cell: ({ value }) => <strong>{fmt.format(num(value))}</strong>,
      },
    ],
    []
  );

  const faturaDetalheColumns = useMemo(
    () => [
      {
        Header: "Item",
        accessor: "item",
        width: 80,
        align: "right",
        sortType: "numPt",
        Cell: ({ value }) => fmt.format(num(value)),
      },
      {
        Header: "Ref. material",
        accessor: "refmaterial",
        width: 140,
      },
      {
        Header: "Material",
        accessor: "material",
        width: 360,
      },
      {
        Header: "Qtd.",
        accessor: "qtd",
        width: 95,
        align: "right",
        sortType: "numPt",
        Cell: ({ value }) => fmt.format(num(value)),
      },
      {
        Header: "Unid.",
        accessor: "qtdunid",
        width: 85,
      },
      {
        Header: "Valor linha €",
        accessor: "valorlinha",
        width: 140,
        align: "right",
        sortType: "numPt",
        Cell: ({ value }) => fmt.format(num(value)),
      },
    ],
    []
  );

  const vendasChartData = useMemo(() => {
    if (!vendasChart) return null;

    return {
      labels: vendasChart.labels,
      datasets: vendasChart.datasets,
    };
  }, [vendasChart]);

  // ======= Carregar dados (só tenta se pode ver) =======
  const carregar = async () => {
    if (!podeVerTabela) return;

    setLoading(true);

    const body = {
      idutilizador: Number(userData?.id || 0),
      refvendedor: isAdminLike ? "" : refUser,
    };

    const [data, err] = await handleApi(api.post(ENDPOINT, body));
    if (err) {
      setErro(err);
      showAlert(err, "error");
      setLoading(false);
      return;
    }
    setRowsData(Array.isArray(data) ? data : []);
    setErro("");
    setLoading(false);
  };

  useEffect(() => {
    const refreshAuto = () => {
      const agora = Date.now();

      // Evita chamadas duplicadas quando focus e visibilitychange disparam quase ao mesmo tempo
      if (agora - ultimoRefreshAutoRef.current < 1500) return;

      ultimoRefreshAutoRef.current = agora;
      refreshDashboardCompleto();
    };

    const handleFocus = () => {
      refreshAuto();
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        refreshAuto();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [podeVerTabela, podeVerKpis, refUser, mesesFechadosAno]);

  useEffect(() => {
    try {
      localStorage.setItem(KPIS_MESES_KEY, String(mesesKpis));
    } catch {}
  }, [mesesKpis]);

  useEffect(() => {
    try {
      localStorage.setItem(KPIS_TOP_KEY, String(topKpis));
    } catch {}
  }, [topKpis]);

  useEffect(() => {
    try {
      localStorage.setItem(KPIS_TOP_ANO_KEY, String(topKpisAno));
    } catch {}
  }, [topKpisAno]);

  // Tenta ler JSON de um blob (quando a API devolve erro com envelope)
  async function tryParseJsonBlob(blob) {
    try {
      const text = await blob.text();
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  async function gerarRelatorio(tipo, par_refvendedor) {
    const loadingKey = `${tipo}:${par_refvendedor}`;
    setLoadingRelatorioSaldo(loadingKey);

    try {
      const res = await api.post(
        "Relatorios/ObterRptSaldosVendedor",
        {
          tipo,
          par_refvendedor,
          idutilizador: Number(userData?.id || 0),
        },
        { responseType: "blob" }
      );

      const contentType = res.headers["content-type"] || "";
      const blob = new Blob([res.data], { type: contentType });

      if (contentType.includes("application/json")) {
        const j = await tryParseJsonBlob(blob);
        const url = j?.data?.url;
        if (url) {
          // iOS → mesma aba; restantes → nova aba
          abrirRelatorio(url);
          return;
        }
        const msg = j?.message || "Erro ao gerar relatório.";
        showAlert(msg, "error");
        return;
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Saldos_${par_refvendedor}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      const msg = e?.message || "Erro de comunicação ao gerar relatório.";
      showAlert(msg, "error");
    } finally {
      setLoadingRelatorioSaldo("");
    }
  }

  async function abrirMais90Pdf() {
    const loadingStartedAt = Date.now();

    try {
      setLoadingMais90Pdf(true);

      const idUtilizador = Number(userData?.id || 0);

      if (!idUtilizador) {
        showAlert("Não foi possível identificar o utilizador.", "error");
        return;
      }

      const temVendedorSelecionado = !!String(refVendedorDebitosSaldos || "").trim();

      const endpoint =
        isAdminLike && !temVendedorSelecionado
          ? "Relatorios/ObterRptMais90Dias"
          : "Relatorios/ObterRptMais90DiasPorVendedor";

      const body =
        isAdminLike && !temVendedorSelecionado
          ? {}
          : {
              idutilizador: idUtilizador,
              refvendedor: temVendedorSelecionado ? refVendedorDebitosSaldos : null,
            };

      const res = await api.post(endpoint, body, { responseType: "blob" });

      const ct = res.headers["content-type"] || "";
      const blob = new Blob([res.data], { type: ct });

      if (ct.includes("application/json")) {
        const txt = await blob.text();

        let j = null;

        try {
          j = JSON.parse(txt);
        } catch {}

        const url = j?.data?.url;

        if (url) {
          abrirRelatorio(url);
          return;
        }

        showAlert(j?.message || "Erro ao gerar relatório.", "error");

        return;
      }
    } catch (e) {
      showAlert(e?.message || "Erro ao gerar relatório de débitos.", "error");
    } finally {
      const elapsed = Date.now() - loadingStartedAt;
      const minLoadingTime = 3500;

      if (elapsed < minLoadingTime) {
        await new Promise((resolve) => setTimeout(resolve, minLoadingTime - elapsed));
      }

      setLoadingMais90Pdf(false);
    }
  }

  const carregarKpi = async () => {
    if (!podeVerKpis) return;

    setLoading(true);

    const body = {
      idutilizador: Number(userData?.id || 0),
      refvendedor: isAdminLike ? "" : refUser,
    };

    const [data, err] = await handleApi(api.post("Dashboard/SumClientesEmAtraso", body));

    setLoading(false);

    if (err) return;

    setKpi({
      sum_91a120: Number(data?.sum_91a120 ?? 0),
      sum_121a150: Number(data?.sum_121a150 ?? 0),
      sum_151mais: Number(data?.sum_151mais ?? 0),
    });
  };

  const carregarUltimaSyncFaturasVendas = async () => {
    const [data, err] = await handleApi(api.post("Dashboard/UltimaSincronizacaoFaturasVendas", {}));

    if (err) {
      setUltimaSyncFaturasVendas(null);
      return;
    }

    setUltimaSyncFaturasVendas(data?.atualizado_em || null);
  };

  const abrirDetalheFatura = useCallback(
    async (row) => {
      const faturaNumero = String(row?.faturanumero ?? "").trim();
      const refVendedor = String(row?.refvendedor ?? "").trim();

      if (!faturaNumero || !refVendedor || !vendasDetalheAno || !vendasDetalheMes) {
        showAlert("Não foi possível identificar a fatura.", "error");
        return;
      }

      setFaturaDetalheSelecionada(row);
      setFaturaDetalheRows([]);
      setFaturaDetalheOpen(true);
      setFaturaDetalheLoading(true);

      const [data, err] = await handleApi(
        api.post("Dashboard/SelVendas3AnosDetalheFatura", {
          idutilizador: Number(userData?.id || 0),
          ano: vendasDetalheAno,
          mes: vendasDetalheMes,
          faturanumero: faturaNumero,
          refvendedor: refVendedor,
        })
      );

      setFaturaDetalheLoading(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      setFaturaDetalheRows(Array.isArray(data) ? data : []);
    },
    [vendasDetalheAno, vendasDetalheMes, userData?.id, showAlert]
  );

  const recarregarDetalheVendasMes = async (novoLimite) => {
    if (!vendasDetalheAno || !vendasDetalheMes) return;

    setVendasDetalheLimite(novoLimite);
    setVendasDetalheLoading(true);

    const podeVerGeral =
      temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

    const body = {
      idutilizador: Number(userData?.id || 0),
      ano: vendasDetalheAno,
      mes: vendasDetalheMes,
      refvendedor: podeVerGeral ? "" : userData?.refvendedor || null,
      limite: novoLimite,
    };

    const [data, err] = await handleApi(api.post("Dashboard/SelVendas3AnosDetalheMes", body));

    setVendasDetalheLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    setVendasDetalheRows(Array.isArray(data) ? data : []);
  };

  const abrirDetalheVendasMes = async ({ dataIndex, datasetLabel, value }) => {
    const ano = Number(datasetLabel);
    const mes = Number(dataIndex) + 1;

    if (!ano || mes < 1 || mes > 12) return;

    setVendasDetalheAno(ano);
    setVendasDetalheMes(mes);
    setVendasDetalheTotalGrafico(Number(value) || 0);
    setVendasDetalheRows([]);
    setVendasDetalheOpen(true);
    setVendasDetalheLoading(true);

    const podeVerGeral =
      temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

    const body = {
      idutilizador: Number(userData?.id || 0),
      ano,
      mes,
      refvendedor: podeVerGeral ? "" : userData?.refvendedor || null,
      limite: vendasDetalheLimite,
    };

    const [data, err] = await handleApi(api.post("Dashboard/SelVendas3AnosDetalheMes", body));

    setVendasDetalheLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    setVendasDetalheRows(Array.isArray(data) ? data : []);
  };

  const abrirComparativoVendasAteDia = async ({ dataIndex }) => {
    setVendasComparativoOpen(true);
    setVendasComparativoLoading(true);
    setVendasComparativoRows([]);
    setVendasComparativoDataApurada(null);
    setVendasComparativoAtualizadoEm(null);

    const podeVerGeral =
      temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

    const body = {
      idutilizador: Number(userData?.id || 0),
      refvendedor: podeVerGeral ? "" : userData?.refvendedor || null,
      mes: Number(dataIndex) + 1,
    };

    const [data, err] = await handleApi(
      api.post("Dashboard/SelVendas3AnosComparativoAteDia", body)
    );

    setVendasComparativoLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    const rows = Array.isArray(data) ? data : [];

    setVendasComparativoRows(rows);
    setVendasComparativoDataApurada(rows?.[0]?.dataapurada ?? null);
    setVendasComparativoAtualizadoEm(rows?.[0]?.atualizado_em ?? null);
  };

  const vendasDetalheRowActions = useMemo(
    () => [
      {
        id: "detalhe-fatura",
        icon: <InfoOutlinedIcon fontSize="small" />,
        tooltip: "Ver detalhe da fatura",
        color: "info",
        onClick: abrirDetalheFatura,
      },
    ],
    [abrirDetalheFatura]
  );

  const carregarVendas = async () => {
    // Apenas utilizadores autorizados podem ver o dashboard geral
    const podeVerGeral =
      temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

    setLoadingVendas(true);

    // Se for Comercial e NÃO fizer parte dos perfis acima → filtro obrigatório
    const body = podeVerGeral
      ? {} // geral
      : { refvendedor: userData?.refvendedor || null };

    const [data, err] = await handleApi(api.post("Dashboard/SelVendas3Anos", body));

    setLoadingVendas(false);

    if (err) return showAlert(err, "error");

    setVendas3Anos(Array.isArray(data) ? data : []);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // AdminLike: pode carregar sem refUser
    const isAdminLike =
      temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

    // Se NÃO for adminLike, precisamos mesmo de refUser (refvendedor) antes de chamar endpoints
    if (!isAdminLike && !refUser) return;

    carregarVendas();
    carregarUltimaSyncFaturasVendas();
  }, [podeVerKpis, refUser]);

  const refreshDashboardCompleto = () => {
    const adminLikeAtual =
      temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

    if (!adminLikeAtual && !refUser) return;

    if (podeVerKpis) {
      carregarVendas();
      carregarUltimaSyncFaturasVendas();
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 88,
        minWidth: 72,
        maxWidth: 112,
        Cell: ({ row }) => {
          const refVendedor = row?.original?.refvendedor;

          const loadingPdf = loadingRelatorioSaldo === `pdf:${refVendedor}`;

          const loadingXlsx = loadingRelatorioSaldo === `xlsx:${refVendedor}`;

          return (
            <div style={{ display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
              <Tooltip title="Abrir PDF">
                <span>
                  <IconButton
                    size="small"
                    color="info"
                    onClick={() => gerarRelatorio("pdf", refVendedor)}
                    disabled={loadingPdf}
                  >
                    {loadingPdf ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <PictureAsPdfIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Exportar Excel">
                <span>
                  <IconButton
                    size="small"
                    color="info"
                    onClick={() => gerarRelatorio("xlsx", refVendedor)}
                    disabled={loadingXlsx}
                  >
                    {loadingXlsx ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <GridOnIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          );
        },
      },

      { Header: "Ref.", accessor: "refvendedor", Filter: TextFilter },
      { Header: "Vendedor", accessor: "vendedor", Filter: TextFilter },
      {
        Header: "Saldo Total",
        accessor: "saldototal",
        Filter: TextFilter,
        Cell: ({ value }) => fmt.format(num(value)),
      },
      {
        Header: "A Vencer",
        accessor: "avencer",
        Filter: TextFilter,
        Cell: ({ value }) => fmt.format(num(value)),
      },
      {
        Header: "Vencido",
        accessor: "vencidototal",
        Filter: TextFilter,
        Cell: ({ value }) => fmt.format(num(value)),
      },
      {
        Header: "1–30",
        accessor: "dias_1a30",
        Filter: TextFilter,
        Cell: ({ value }) => fmt.format(num(value)),
      },
      {
        Header: "31–60",
        accessor: "dias_31a60",
        Filter: TextFilter,
        Cell: ({ value }) => fmt.format(num(value)),
      },
      {
        Header: "61–90",
        accessor: "dias_61a90",
        Filter: TextFilter,
        Cell: ({ value }) => fmt.format(num(value)),
      },
      {
        Header: "91–120",
        accessor: "dias_91a120",
        Filter: TextFilter,
        Cell: ({ value }) => fmt.format(num(value)),
      },
      {
        Header: "121–150",
        accessor: "dias_121a150",
        Filter: TextFilter,
        Cell: ({ value }) => fmt.format(num(value)),
      },
      {
        Header: "151+",
        accessor: "dias_151mais",
        Filter: TextFilter,
        Cell: ({ value }) => fmt.format(num(value)),
      },
    ],
    // DEPS — evita “stale memo” das permissões
    [isAdminLike, isComercial, refUser, loadingRelatorioSaldo]
  );

  // ======= Tabela =======
  const dataFiltrada = useMemo(() => {
    if (!podeVerTabela) return [];

    return rowsData;
  }, [rowsData, podeVerTabela]);

  const table = useTable(
    {
      columns,
      data: dataFiltrada,
      initialState: { filters: initialFilters },
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setAllFilters, state } =
    table;

  // gerir persistência de filtros
  useEffect(() => {
    if (!keepFilters && (state.filters?.length || 0) > 0) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [rowsData]); // eslint-disable-line

  useEffect(() => {
    try {
      localStorage.setItem(KEEP_KEY, JSON.stringify(keepFilters));
    } catch {}
    if (!keepFilters) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [keepFilters, setAllFilters]);

  useEffect(() => {
    if (!keepFilters) return;
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify(state.filters || []));
    } catch {}
  }, [state.filters, keepFilters]);

  const vendasDetalheTotalLinhas = vendasDetalheRows.reduce(
    (acc, row) => acc + Number(row?.valorfatura || 0),
    0
  );

  const faturaDetalheTotalLinhas = faturaDetalheRows.reduce(
    (acc, row) => acc + Number(row?.valorlinha || 0),
    0
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />

      {ultimaSyncFaturasVendas && (
        <MDBox
          mb={1.5}
          display="flex"
          justifyContent={{ xs: "flex-start", md: "flex-end" }}
          sx={{ width: "100%" }}
        >
          <MDTypography
            variant="caption"
            color="text"
            sx={{
              fontSize: 12,
              fontWeight: 500,
              whiteSpace: "normal",
            }}
          >
            Dados de vendas atualizados em{" "}
            <strong>
              {new Date(ultimaSyncFaturasVendas).toLocaleString("pt-PT", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </strong>
          </MDTypography>
        </MDBox>
      )}

      {podeVerKpis && (
        <DashboardLazyAccordion
          title="Vendas dos últimos 3 anos (sem IVA e sem vasilhame)"
          subtitle="Totais anuais indicados na legenda. Valores em euros, sempre sem IVA."
          loading={loadingVendas}
          loaded
          initialExpanded
          showRefreshButton={false}
        >
          {loadingVendas || !vendasChart ? (
            <MDBox py={4} display="flex" justifyContent="center">
              <CircularProgress size={24} />
            </MDBox>
          ) : (
            <>
              <MDBox mb={1.5}>
                <MemoizedDefaultLineChart
                  chart={vendasChartData}
                  height="16rem"
                  interactiveTooltip
                  onTooltipInfoClick={abrirDetalheVendasMes}
                  onTooltipTrendClick={abrirComparativoVendasAteDia}
                  showTooltipTrendButton={({ dataIndex, datasetLabel }) => {
                    const hoje = new Date();

                    return (
                      Number(datasetLabel) === hoje.getFullYear() &&
                      Number(dataIndex) <= hoje.getMonth()
                    );
                  }}
                />
              </MDBox>

              <MDBox display="flex" flexWrap="wrap" gap={2} mb={0.5}>
                {vendasChart.datasets.map((ds) => {
                  const ano = ds.label.split("–")[0].trim();
                  const totalAno = vendasChart.totaisAno?.[ano] ?? 0;

                  return (
                    <MDTypography
                      key={ds.label}
                      variant="button"
                      color="text"
                      sx={{
                        fontSize: 12,
                        display: "inline-flex",
                        alignItems: "center",
                        mr: 1.5,
                      }}
                    >
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          marginRight: 6,
                          backgroundColor: colorMap[ds.color] || "#999",
                          display: "inline-block",
                        }}
                      />
                      {ano}
                      <span style={{ marginLeft: 4 }}>
                        ( <strong>{fmt.format(totalAno)} €</strong> )
                      </span>
                    </MDTypography>
                  );
                })}
              </MDBox>

              <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.5 }}>
                Tipo de somatório:{" "}
                <strong>
                  {vendasChart.tipo === "Geral"
                    ? "Geral (todos os vendedores)"
                    : "Comercial (apenas o vendedor autenticado)"}
                </strong>
              </MDTypography>
            </>
          )}
        </DashboardLazyAccordion>
      )}

      <DashboardLazyAccordion
        title="Objetivos"
        subtitle="Objetivos ativos ou recentes e respetivo progresso."
        buttonLabel="Atualizar"
        loading={loadingObjetivos}
        loaded={objetivosCarregados}
        onRefresh={atualizarObjetivos}
      >
        <ObjetivosEstadoDashboard
          autoLoad={false}
          refreshToken={objetivosRefreshToken}
          onLoadingChange={setLoadingObjetivos}
          onLoadedChange={setObjetivosCarregados}
        />
      </DashboardLazyAccordion>

      {isAdminLike && (
        <DashboardLazyAccordion
          title="Objetivos v2"
          subtitle="Objetivos V2 ativos ou recentes e respetivo progresso."
          buttonLabel="Atualizar"
          loading={loadingObjetivosV2}
          loaded={objetivosV2Carregados}
          onRefresh={atualizarObjetivosV2}
        >
          <ObjetivosV2EstadoDashboard
            autoLoad={false}
            refreshToken={objetivosV2RefreshToken}
            onLoadingChange={setLoadingObjetivosV2}
            onLoadedChange={setObjetivosV2Carregados}
          />
        </DashboardLazyAccordion>
      )}

      <DashboardLazyAccordion
        title="Incentivos"
        subtitle="Incentivos ativos e posição atual do vendedor."
        buttonLabel="Atualizar"
        loading={loadingIncentivos}
        loaded={incentivosCarregados}
        empty={incentivosVazios}
        emptyMessage="Não foram encontrados incentivos para este utilizador."
        onRefresh={atualizarIncentivos}
      >
        <IncentivosVendedorDashboard
          autoLoad={false}
          refreshToken={incentivosRefreshToken}
          onLoadingChange={setLoadingIncentivos}
          onLoadedChange={setIncentivosCarregados}
          onEmptyChange={setIncentivosVazios}
        />
      </DashboardLazyAccordion>

      <DashboardLazyAccordion
        title={`KPIs dos últimos ${mesesKpis} meses`}
        subtitle={`Compara os últimos ${mesesKpis} meses com os ${mesesKpis} meses anteriores.`}
        buttonLabel="Atualizar"
        loading={loadingKpis12Meses}
        loaded={kpis12MesesCarregados}
        onRefresh={atualizarKpis12Meses}
        headerContent={
          <MDBox
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            sx={{
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <MDTypography variant="button" color="text" sx={{ whiteSpace: "nowrap" }}>
              Meses:
            </MDTypography>

            <input
              type="text"
              value={mesesKpis}
              onChange={(e) => setMesesKpis(normalizarKpisMeses(e.target.value))}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 48,
                textAlign: "center",
                padding: "6px 8px",
                border: "1px solid rgba(0,0,0,0.2)",
                borderRadius: 6,
                fontSize: 13,
                outline: "none",
                background: "#fff",
              }}
            />

            <MDTypography variant="caption" color="text" sx={{ whiteSpace: "nowrap" }}>
              (Máx.: <strong>24</strong>)
            </MDTypography>

            <MDBox
              sx={{
                width: "1px",
                height: 28,
                bgcolor: "divider",
                mx: 1,
                display: { xs: "none", sm: "block" },
              }}
            />

            <MDTypography variant="button" color="text" sx={{ whiteSpace: "nowrap" }}>
              Top:
            </MDTypography>

            <select
              value={topKpis}
              onChange={(e) => setTopKpis(Number(e.target.value))}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 64,
                padding: "6px 8px",
                border: "1px solid rgba(0,0,0,0.2)",
                borderRadius: 6,
                fontSize: 13,
                outline: "none",
                background: "#fff",
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={40}>40</option>
              <option value={50}>50</option>
            </select>
          </MDBox>
        }
      >
        <MDBox
          display="grid"
          gridTemplateColumns={{
            xs: "1fr",
            md: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(4, minmax(0, 1fr))",
          }}
          gap={2}
          sx={{ width: "100%" }}
        >
          <Card sx={{ cursor: "pointer" }} onClick={abrirTop20Parceiros}>
            <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
              {/* Badge (igual ao débitos) */}
              <MDBox
                sx={({ palette, functions }) => ({
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: functions.linearGradient(
                    palette.corbase1.main,
                    palette.corbase1.focus
                  ),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "none",
                  mr: 1.5,
                })}
              >
                <EmojiEventsIcon sx={{ color: "#fff", fontSize: 20 }} />
              </MDBox>

              {/* Conteúdo principal */}
              <MDBox flex={1}>
                <MDTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  sx={{ whiteSpace: "normal" }}
                >
                  Top Parceiros
                </MDTypography>

                <MDTypography variant="h5" fontWeight="bold">
                  {fmt.format(num(topParceirosTotal))} €
                </MDTypography>

                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>
                  Top {topKpis} (últimos {mesesKpis} meses)
                </MDTypography>
              </MDBox>

              {/* “…” / detalhe */}
              <Tooltip title={`Ver Top ${topKpis}`}>
                <LeaderboardIcon color="info" />
              </Tooltip>
            </MDBox>

            {/* Rodapé (igual ao débitos: título verde + caption) */}
            {/* <MDBox px={2} pb={2} pt={0.5}>
            <MDTypography
              variant="button"
              sx={{ color: "success.main", fontWeight: "bold", display: "block", mb: 0 }}
            >
              Pódio (Top 3)
            </MDTypography>

            <MDBox mt={0.5}>
              {topParceiros.map((p, idx) => (
                <MDTypography key={p.ref} variant="caption" color="text" sx={{ display: "block" }}>
                  {idx + 1}º {p.nome || p.ref} — <strong>{fmt.format(num(p.total_atual))}</strong>
                </MDTypography>
              ))}

              <MDTypography
                variant="caption"
                color="info"
                sx={{ display: "block", mt: 0.5, fontWeight: "bold" }}
              >
                …
              </MDTypography>
            </MDBox>
          </MDBox> */}
          </Card>

          <Card sx={{ cursor: "pointer" }} onClick={abrirTop20Clientes}>
            <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
              <MDBox
                sx={({ palette, functions }) => ({
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: functions.linearGradient(
                    palette.corbase1.main,
                    palette.corbase1.focus
                  ),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "none",
                  mr: 1.5,
                })}
              >
                <EmojiEventsIcon sx={{ color: "#fff", fontSize: 20 }} />
              </MDBox>

              <MDBox flex={1}>
                <MDTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  sx={{ whiteSpace: "normal" }}
                >
                  Top Clientes
                </MDTypography>

                <MDTypography variant="h5" fontWeight="bold">
                  {fmt.format(num(topClientesTotal))} €
                </MDTypography>

                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>
                  Top {topKpis} (últimos {mesesKpis} meses)
                </MDTypography>
              </MDBox>

              <Tooltip title={`Ver Top ${topKpis}`}>
                <LeaderboardIcon color="info" />
              </Tooltip>
            </MDBox>

            {/* <MDBox px={2} pb={2} pt={0.5}>
            <MDTypography
              variant="button"
              sx={{ color: "success.main", fontWeight: "bold", display: "block", mb: 0 }}
            >
              Pódio (Top 3)
            </MDTypography>

            <MDBox mt={0.5}>
              {topClientes.map((c, idx) => (
                <MDTypography key={c.ref} variant="caption" color="text" sx={{ display: "block" }}>
                  {idx + 1}º {c.nome || c.ref} — <strong>{fmt.format(num(c.total_atual))}</strong>
                </MDTypography>
              ))}

              <MDTypography
                variant="caption"
                color="info"
                sx={{ display: "block", mt: 0.5, fontWeight: "bold" }}
              >
                …
              </MDTypography>
            </MDBox>
          </MDBox> */}
          </Card>

          <Card sx={{ cursor: "pointer" }} onClick={abrirTop20Subiram}>
            <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
              <MDBox
                sx={({ palette, functions }) => ({
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: functions.linearGradient(
                    palette.corbase1.main,
                    palette.corbase1.focus
                  ),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 1.5,
                })}
              >
                <TrendingUpIcon sx={{ color: "#fff", fontSize: 20 }} />
              </MDBox>

              <MDBox flex={1}>
                <MDTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  sx={{ whiteSpace: "normal" }}
                >
                  Clientes que mais subiram
                </MDTypography>

                <MDTypography variant="h5" fontWeight="bold">
                  {fmt.format(num(topSubiramTotal))} €
                </MDTypography>

                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>
                  Ganho Top {topKpis} (dif. {mesesKpis}m vs {mesesKpis}m ant.)
                </MDTypography>
              </MDBox>

              <Tooltip title={`Ver Top ${topKpis}`}>
                <span>
                  <IconButton
                    size="small"
                    color="info"
                    onClick={(e) => {
                      e.stopPropagation();
                      abrirTop20Subiram();
                    }}
                  >
                    <LeaderboardIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </MDBox>

            {/* <MDBox px={2} pb={2} pt={0.5}>
            <MDTypography
              variant="button"
              sx={{ color: "success.main", fontWeight: "bold", display: "block" }}
            >
              Pódio (Top 3)
            </MDTypography>

            <MDBox mt={0.5}>
              {topSubiram.map((c, idx) => (
                <MDTypography key={c.ref} variant="caption" color="text" sx={{ display: "block" }}>
                  {idx + 1}º {c.nome || c.ref} — <strong>{fmt.format(num(c.dif))}</strong>
                </MDTypography>
              ))}

              <MDTypography
                variant="caption"
                color="info"
                sx={{ display: "block", mt: 0.5, fontWeight: "bold" }}
              >
                …
              </MDTypography>
            </MDBox>
          </MDBox> */}
          </Card>

          <Card sx={{ cursor: "pointer" }} onClick={abrirTop20Desceram}>
            <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
              <MDBox
                sx={({ palette, functions }) => ({
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: functions.linearGradient(
                    palette.corbase1.main,
                    palette.corbase1.focus
                  ),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 1.5,
                })}
              >
                <TrendingDownIcon sx={{ color: "#fff", fontSize: 22 }} />
              </MDBox>

              <MDBox flex={1}>
                <MDTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  sx={{ whiteSpace: "normal" }}
                >
                  Clientes que mais desceram
                </MDTypography>

                <MDTypography variant="h5" fontWeight="bold">
                  {fmt.format(num(topDesceramTotal))} €
                </MDTypography>

                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>
                  Perda Top {topKpis} (dif. {mesesKpis}m vs {mesesKpis}m ant.)
                </MDTypography>
              </MDBox>

              <Tooltip title={`Ver Top ${topKpis}`}>
                <span>
                  <IconButton
                    size="small"
                    color="info"
                    onClick={(e) => {
                      e.stopPropagation();
                      abrirTop20Desceram();
                    }}
                  >
                    <LeaderboardIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </MDBox>
          </Card>
        </MDBox>
      </DashboardLazyAccordion>

      <DashboardLazyAccordion
        title="KPIs anuais por meses fechados"
        subtitle={
          <>
            Compara janeiro até ao último mês terminado escolhido, entre{" "}
            <strong>{anoAtualAnalise}</strong> e <strong>{anoAnteriorAnalise}</strong>.
          </>
        }
        buttonLabel="Atualizar"
        loading={loadingKpisAno}
        loaded={kpisAnoCarregados}
        onRefresh={atualizarKpisAno}
        headerContent={
          <MDBox
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            sx={{
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <MDTypography variant="button" color="text" sx={{ whiteSpace: "nowrap" }}>
              Meses terminados:
            </MDTypography>

            <input
              type="text"
              value={mesesFechadosAno}
              onChange={(e) => setMesesFechadosAno(normalizarMesesFechadosAno(e.target.value))}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 48,
                textAlign: "center",
                padding: "6px 8px",
                border: "1px solid rgba(0,0,0,0.2)",
                borderRadius: 6,
                fontSize: 13,
                outline: "none",
                background: "#fff",
              }}
            />

            <MDTypography variant="caption" color="text" sx={{ whiteSpace: "nowrap" }}>
              (Máx.: <strong>{getMaxMesesFechadosAno()}</strong>)
            </MDTypography>

            <MDBox
              sx={{
                width: "1px",
                height: 28,
                bgcolor: "divider",
                mx: 1,
                display: { xs: "none", sm: "block" },
              }}
            />

            <MDTypography variant="button" color="text" sx={{ whiteSpace: "nowrap" }}>
              Top:
            </MDTypography>

            <select
              value={topKpisAno}
              onChange={(e) => setTopKpisAno(Number(e.target.value))}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 64,
                padding: "6px 8px",
                border: "1px solid rgba(0,0,0,0.2)",
                borderRadius: 6,
                fontSize: 13,
                outline: "none",
                background: "#fff",
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={40}>40</option>
              <option value={50}>50</option>
            </select>
          </MDBox>
        }
      >
        <MDBox
          display="grid"
          gridTemplateColumns={{
            xs: "1fr",
            md: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(4, minmax(0, 1fr))",
          }}
          gap={2}
          sx={{ width: "100%" }}
        >
          <Card sx={{ cursor: "pointer" }} onClick={abrirTop20ParceirosAno}>
            <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
              <MDBox
                sx={({ palette, functions }) => ({
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: functions.linearGradient(
                    palette.corbase1.main,
                    palette.corbase1.focus
                  ),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "none",
                  mr: 1.5,
                })}
              >
                <EmojiEventsIcon sx={{ color: "#fff", fontSize: 20 }} />
              </MDBox>

              <MDBox flex={1}>
                <MDTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  sx={{ whiteSpace: "normal" }}
                >
                  Top Parceiros
                </MDTypography>

                <MDTypography variant="h5" fontWeight="bold">
                  {fmt.format(num(topParceirosAnoTotal))} €
                </MDTypography>

                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>
                  Top {topKpisAno} ({anoAtualAnalise} Jan-{mesLabel} vs {anoAnteriorAnalise} Jan-
                  {mesLabel})
                </MDTypography>
              </MDBox>

              <Tooltip title={`Ver Top ${topKpisAno}`}>
                <LeaderboardIcon color="info" />
              </Tooltip>
            </MDBox>
          </Card>

          <Card sx={{ cursor: "pointer" }} onClick={abrirTop20ClientesAno}>
            <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
              <MDBox
                sx={({ palette, functions }) => ({
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: functions.linearGradient(
                    palette.corbase1.main,
                    palette.corbase1.focus
                  ),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "none",
                  mr: 1.5,
                })}
              >
                <EmojiEventsIcon sx={{ color: "#fff", fontSize: 20 }} />
              </MDBox>

              <MDBox flex={1}>
                <MDTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  sx={{ whiteSpace: "normal" }}
                >
                  Top Clientes
                </MDTypography>

                <MDTypography variant="h5" fontWeight="bold">
                  {fmt.format(num(topClientesAnoTotal))} €
                </MDTypography>

                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>
                  Top {topKpisAno} ({anoAtualAnalise} Jan-{mesLabel} vs {anoAnteriorAnalise} Jan-
                  {mesLabel})
                </MDTypography>
              </MDBox>

              <Tooltip title={`Ver Top ${topKpisAno}`}>
                <LeaderboardIcon color="info" />
              </Tooltip>
            </MDBox>
          </Card>

          <Card sx={{ cursor: "pointer" }} onClick={abrirTop20SubiramAno}>
            <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
              <MDBox
                sx={({ palette, functions }) => ({
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: functions.linearGradient(
                    palette.corbase1.main,
                    palette.corbase1.focus
                  ),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 1.5,
                })}
              >
                <TrendingUpIcon sx={{ color: "#fff", fontSize: 20 }} />
              </MDBox>

              <MDBox flex={1}>
                <MDTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  sx={{ whiteSpace: "normal" }}
                >
                  Clientes que mais subiram
                </MDTypography>

                <MDTypography variant="h5" fontWeight="bold">
                  {fmt.format(num(topSubiramAnoTotal))} €
                </MDTypography>

                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>
                  Ganho Top {topKpisAno} ({anoAtualAnalise} Jan-{mesLabel} vs {anoAnteriorAnalise}{" "}
                  Jan-
                  {mesLabel})
                </MDTypography>
              </MDBox>

              <Tooltip title={`Ver Top ${topKpisAno}`}>
                <span>
                  <IconButton
                    size="small"
                    color="info"
                    onClick={(e) => {
                      e.stopPropagation();
                      abrirTop20SubiramAno();
                    }}
                  >
                    <LeaderboardIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </MDBox>
          </Card>

          <Card sx={{ cursor: "pointer" }} onClick={abrirTop20DesceramAno}>
            <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
              <MDBox
                sx={({ palette, functions }) => ({
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: functions.linearGradient(
                    palette.corbase1.main,
                    palette.corbase1.focus
                  ),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 1.5,
                })}
              >
                <TrendingDownIcon sx={{ color: "#fff", fontSize: 22 }} />
              </MDBox>

              <MDBox flex={1}>
                <MDTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  sx={{ whiteSpace: "normal" }}
                >
                  Clientes que mais desceram
                </MDTypography>

                <MDTypography variant="h5" fontWeight="bold">
                  {fmt.format(num(topDesceramAnoTotal))} €
                </MDTypography>

                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>
                  Perda Top {topKpisAno} ({anoAtualAnalise} Jan-{mesLabel} vs {anoAnteriorAnalise}{" "}
                  Jan-
                  {mesLabel})
                </MDTypography>
              </MDBox>

              <Tooltip title={`Ver Top ${topKpisAno}`}>
                <span>
                  <IconButton
                    size="small"
                    color="info"
                    onClick={(e) => {
                      e.stopPropagation();
                      abrirTop20DesceramAno();
                    }}
                  >
                    <LeaderboardIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </MDBox>
          </Card>
        </MDBox>
      </DashboardLazyAccordion>

      {!podeVerRuturas ? null : (
        <DashboardLazyAccordion
          title="Materiais com risco de rutura"
          subtitle="Materiais com stock reduzido ou em risco de rutura nos centros selecionados."
          buttonLabel="Atualizar"
          loading={loadingMateriaisRisco}
          loaded={materiaisRiscoCarregados}
          empty={materiaisRiscoVazios}
          emptyMessage="Não foram encontrados materiais com risco de rutura."
          onRefresh={atualizarMateriaisRisco}
        >
          <MateriaisRiscoDashboard
            podeVerRuturas={podeVerRuturas}
            autoLoad={false}
            refreshToken={materiaisRiscoRefreshToken}
            onLoadingChange={setLoadingMateriaisRisco}
            onLoadedChange={setMateriaisRiscoCarregados}
            onEmptyChange={setMateriaisRiscoVazios}
          />
        </DashboardLazyAccordion>
      )}

      <DashboardLazyAccordion
        title="Vendas mensais por vendedor"
        subtitle="Compara as vendas mensais dos últimos 13 meses com o período homólogo anterior, para análise individual da equipa comercial."
        buttonLabel="Atualizar"
        loading={loadingVendasVendedores}
        loaded={vendasVendedoresCarregadas}
        empty={vendasVendedoresVazias}
        emptyMessage="Não foram encontrados dados para apresentar."
        onRefresh={atualizarVendasVendedores}
      >
        <VendasMensaisVendedoresDashboard
          autoLoad={false}
          refreshToken={vendasVendedoresRefreshToken}
          onLoadingChange={setLoadingVendasVendedores}
          onLoadedChange={setVendasVendedoresCarregadas}
          onEmptyChange={setVendasVendedoresVazias}
        />
      </DashboardLazyAccordion>

      {!podeVerTabela ? null : (
        <DashboardLazyAccordion
          title="Débitos de clientes e saldos por vendedor"
          subtitle="Resumo dos clientes em atraso e dos saldos por vendedor."
          buttonLabel="Atualizar"
          loading={loadingDebitosSaldos}
          loaded={debitosSaldosCarregados}
          onRefresh={atualizarDebitosSaldos}
          headerContent={
            vendedoresDebitosSaldos.length > 1 ? (
              <MDBox
                onClick={(e) => e.stopPropagation()}
                sx={{
                  width: { xs: "100%", sm: 320 },
                }}
              >
                <select
                  value={refVendedorDebitosSaldos}
                  onChange={async (e) => {
                    const novaRef = e.target.value;

                    setRefVendedorDebitosSaldos(novaRef);
                    setLoadingDebitosSaldos(true);

                    try {
                      if (!novaRef) {
                        await Promise.all([carregarKpi(), carregar()]);
                      } else {
                        await Promise.all([
                          carregarKpiPorVendedor(novaRef),
                          carregarSaldosPorVendedor(novaRef),
                        ]);
                      }
                    } finally {
                      setLoadingDebitosSaldos(false);
                    }
                  }}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: 6,
                    fontSize: 13,
                    border: "1px solid #ccc",
                    background: "#fff",
                  }}
                >
                  <option value="">Todos</option>
                  {vendedoresDebitosSaldos.map((v) => (
                    <option key={v.ref} value={v.ref}>
                      {v.nome} ({v.ref})
                    </option>
                  ))}
                </select>
              </MDBox>
            ) : null
          }
        >
          {/* === KPIs de Atrasos === */}
          <MDBox
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(220px, 1fr))"
            gap={2}
            mb={2}
            sx={{ width: "100%" }}
          >
            {/* 90+ */}
            <Card>
              <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
                <MDBox
                  sx={({ palette, functions }) => ({
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: functions.linearGradient(
                      palette.corbase1.main,
                      palette.corbase1.focus
                    ),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "none",
                    mr: 1.5,
                  })}
                >
                  <MDTypography component="span" color="white" fontSize={13} lineHeight={1}>
                    90+
                  </MDTypography>
                </MDBox>

                <MDBox flex={1}>
                  <MDTypography
                    variant="button"
                    color="text"
                    fontWeight="regular"
                    sx={{ whiteSpace: "normal" }}
                  >
                    De 91 a 120 dias
                  </MDTypography>

                  <MDTypography variant="h5" fontWeight="bold">
                    {fmt.format(num(kpi.sum_91a120))}
                  </MDTypography>
                </MDBox>

                <Tooltip title="Abrir PDF">
                  <span>
                    <IconButton
                      size="small"
                      color="info"
                      onClick={abrirMais90Pdf}
                      disabled={loadingMais90Pdf}
                    >
                      {loadingMais90Pdf ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <PictureAsPdfIcon />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </MDBox>

              <MDBox px={2} pb={2} pt={1}>
                <MDTypography
                  variant="button"
                  sx={{
                    color: "success.main",
                    fontWeight: "bold",
                    display: "block",
                    mb: 0,
                  }}
                >
                  Débitos de clientes: De 91 a 120 dias
                </MDTypography>

                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{ display: "block", mt: 0.5, whiteSpace: "normal" }}
                >
                  Clique no botão PDF, para mais detalhes.
                </MDTypography>
              </MDBox>
            </Card>

            {/* 120+ */}
            <Card>
              <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
                <MDBox
                  sx={({ palette, functions }) => ({
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: functions.linearGradient(
                      palette.corbase1.main,
                      palette.corbase1.focus
                    ),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "none",
                    mr: 1.5,
                  })}
                >
                  <MDTypography component="span" color="white" fontSize={13} lineHeight={1}>
                    120+
                  </MDTypography>
                </MDBox>

                <MDBox flex={1}>
                  <MDTypography
                    variant="button"
                    color="text"
                    fontWeight="regular"
                    sx={{ whiteSpace: "normal" }}
                  >
                    De 121 a 150 dias
                  </MDTypography>

                  <MDTypography variant="h5" fontWeight="bold">
                    {fmt.format(num(kpi.sum_121a150))}
                  </MDTypography>
                </MDBox>

                <Tooltip title="Abrir PDF">
                  <span>
                    <IconButton
                      size="small"
                      color="info"
                      onClick={abrirMais90Pdf}
                      disabled={loadingMais90Pdf}
                    >
                      {loadingMais90Pdf ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <PictureAsPdfIcon />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </MDBox>

              <MDBox px={2} pb={2} pt={1}>
                <MDTypography
                  variant="button"
                  sx={{
                    color: "success.main",
                    fontWeight: "bold",
                    display: "block",
                    mb: 0,
                  }}
                >
                  Débitos de clientes: De 121 a 150 dias
                </MDTypography>

                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{ display: "block", mt: 0.5, whiteSpace: "normal" }}
                >
                  Clique no botão PDF, para mais detalhes.
                </MDTypography>
              </MDBox>
            </Card>

            {/* 150+ */}
            <Card>
              <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
                <MDBox
                  sx={({ palette, functions }) => ({
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: functions.linearGradient(
                      palette.corbase1.main,
                      palette.corbase1.focus
                    ),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "none",
                    mr: 1.5,
                  })}
                >
                  <MDTypography component="span" color="white" fontSize={13} lineHeight={1}>
                    150+
                  </MDTypography>
                </MDBox>

                <MDBox flex={1}>
                  <MDTypography
                    variant="button"
                    color="text"
                    fontWeight="regular"
                    sx={{ whiteSpace: "normal" }}
                  >
                    Mais de 150 dias
                  </MDTypography>

                  <MDTypography variant="h5" fontWeight="bold">
                    {fmt.format(num(kpi.sum_151mais))}
                  </MDTypography>
                </MDBox>

                <Tooltip title="Abrir PDF">
                  <span>
                    <IconButton
                      size="small"
                      color="info"
                      onClick={abrirMais90Pdf}
                      disabled={loadingMais90Pdf}
                    >
                      {loadingMais90Pdf ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <PictureAsPdfIcon />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </MDBox>

              <MDBox px={2} pb={2} pt={1}>
                <MDTypography
                  variant="button"
                  sx={{
                    color: "success.main",
                    fontWeight: "bold",
                    display: "block",
                    mb: 0,
                  }}
                >
                  Débitos de clientes: Pendentes com mais de 150 dias
                </MDTypography>

                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{ display: "block", mt: 0.5, whiteSpace: "normal" }}
                >
                  Clique no botão PDF, para mais detalhes.
                </MDTypography>
              </MDBox>
            </Card>
          </MDBox>

          {/* === Saldos por vendedor === */}
          <Card>
            <MDBox p={2}>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: "stretch", md: "center" }}
                gap={1}
                flexWrap="wrap"
                mb={2}
              >
                <MDTypography variant="h6">Saldos por vendedor</MDTypography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={keepFilters}
                      onChange={(e) => setKeepFilters(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Manter filtros"
                  sx={{ m: 0 }}
                />
              </MDBox>

              <MDBox sx={{ maxHeight: "40vh", overflow: "auto", borderRadius: 1 }}>
                <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    {headerGroups.map((hg) => (
                      <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                        {hg.headers.map((col) => (
                          <th
                            {...col.getHeaderProps(col.getSortByToggleProps())}
                            key={col.id}
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 2,
                              background: "#fff",
                              textAlign: "left",
                              padding: "6px 8px",
                              fontSize: 12,
                              color: "#7b809a",
                              verticalAlign: "bottom",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                              width: col.id === "acoes" ? col.width : undefined,
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center" }}>
                              {col.render("Header")}
                              {col.isSorted ? (
                                col.isSortedDesc ? (
                                  <span style={{ marginLeft: 6 }}>▼</span>
                                ) : (
                                  <span style={{ marginLeft: 6 }}>▲</span>
                                )
                              ) : null}
                            </div>

                            {!col.disableFilters && (
                              <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                                {col.canFilter ? col.render("Filter") : null}
                              </div>
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>

                  <tbody {...getTableBodyProps()}>
                    {rows.map((row) => {
                      prepareRow(row);

                      return (
                        <tr {...row.getRowProps()} key={row.id}>
                          {row.cells.map((cell) => (
                            <td
                              {...cell.getCellProps()}
                              key={cell.column.id}
                              style={{
                                padding: "8px 10px",
                                borderTop: "1px solid #eee",
                                fontSize: 12,
                                color: "#1a2b4c",
                                whiteSpace: "nowrap",
                                width: cell.column.id === "acoes" ? cell.column.width : undefined,
                              }}
                            >
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}

                    {!rows.length && (
                      <tr>
                        <td
                          colSpan={columns.length}
                          style={{
                            padding: "12px 10px",
                            fontSize: 12,
                            color: "#7b809a",
                          }}
                        >
                          Sem dados para apresentar.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </MDBox>

              {erro && (
                <MDBox mt={1}>
                  <MDTypography variant="button" color="error">
                    {erro}
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          </Card>
        </DashboardLazyAccordion>
      )}

      <Dialog
        open={vendasComparativoOpen}
        onClose={() => setVendasComparativoOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {vendasComparativoDataApurada &&
          new Date(vendasComparativoDataApurada).getMonth() === new Date().getMonth()
            ? "Comparativo de vendas até à data apurada"
            : `Comparativo de vendas — ${getMesLabel(
                vendasComparativoDataApurada
                  ? new Date(vendasComparativoDataApurada).getMonth() + 1
                  : null
              )}`}
        </DialogTitle>

        <DialogContent dividers>
          {vendasComparativoLoading ? (
            <MDBox py={4} display="flex" justifyContent="center">
              <CircularProgress size={24} />
            </MDBox>
          ) : (
            <>
              {vendasComparativoDataApurada && (
                <MDTypography variant="button" color="text" sx={{ display: "block", mb: 2 }}>
                  {new Date(vendasComparativoDataApurada).getMonth() === new Date().getMonth() ? (
                    <>
                      Valores comparados até{" "}
                      <strong>
                        {new Date(vendasComparativoDataApurada).toLocaleDateString("pt-PT")}
                      </strong>
                      {vendasComparativoAtualizadoEm && (
                        <>
                          , com dados atualizados às{" "}
                          <strong>
                            {new Date(vendasComparativoAtualizadoEm).toLocaleTimeString("pt-PT", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </strong>
                        </>
                      )}
                      .
                    </>
                  ) : (
                    <>
                      Comparação do mês completo de{" "}
                      <strong>
                        {getMesLabel(new Date(vendasComparativoDataApurada).getMonth() + 1)}{" "}
                        {new Date(vendasComparativoDataApurada).getFullYear()}
                      </strong>
                      .
                    </>
                  )}
                </MDTypography>
              )}

              <MDBox
                sx={{
                  overflowX: "auto",
                  maxWidth: "100%",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    minWidth: 620,
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "10px 12px",
                          fontSize: 12,
                          color: "#7b809a",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        Período
                      </th>

                      {vendasComparativoRows.map((row) => (
                        <th
                          key={row.ano}
                          style={{
                            textAlign: "right",
                            padding: "10px 12px",
                            fontSize: 12,
                            color: "#7b809a",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          {row.ano}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {["mes_ate_dia", "ano_ate_dia"].map((campo) => {
                      const atual = vendasComparativoRows?.[0];
                      const valorAtual = Number(atual?.[campo] || 0);

                      const dataApurada = vendasComparativoDataApurada
                        ? new Date(vendasComparativoDataApurada)
                        : null;

                      const mesCorrente =
                        dataApurada !== null && dataApurada.getMonth() === new Date().getMonth();

                      const mesLabel = dataApurada ? getMesLabel(dataApurada.getMonth() + 1) : "";

                      const diaApurado = dataApurada ? dataApurada.getDate() : "";

                      const labelPeriodo =
                        campo === "mes_ate_dia"
                          ? mesCorrente
                            ? `${mesLabel} até dia ${diaApurado}`
                            : `${mesLabel} completo`
                          : mesCorrente
                          ? `Jan → ${diaApurado} ${mesLabel}`
                          : `Jan → ${mesLabel}`;

                      return (
                        <tr key={campo}>
                          <td
                            style={{
                              padding: "12px",
                              fontSize: 13,
                              borderBottom: "1px solid #eee",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {labelPeriodo}
                          </td>

                          {vendasComparativoRows.map((row, index) => {
                            const valor = Number(row?.[campo] || 0);
                            const diferenca = valorAtual - valor;
                            const percentagem = valor !== 0 ? (diferenca / valor) * 100 : null;

                            const corDiferenca =
                              diferenca > 0 ? "#16a34a" : diferenca < 0 ? "#dc2626" : "inherit";

                            return (
                              <td
                                key={row.ano}
                                style={{
                                  padding: "12px",
                                  fontSize: 13,
                                  textAlign: "right",
                                  borderBottom: "1px solid #eee",
                                  verticalAlign: "top",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {index === 0 ? (
                                  <strong>{fmt.format(valor)} €</strong>
                                ) : (
                                  <>
                                    <div>{fmt.format(valor)} €</div>

                                    <div style={{ marginTop: 3 }}>
                                      <strong style={{ color: corDiferenca }}>
                                        ({diferenca >= 0 ? "+" : ""}
                                        {fmt.format(diferenca)} €
                                        {percentagem !== null
                                          ? ` / ${percentagem >= 0 ? "+" : ""}${percentagem
                                              .toFixed(2)
                                              .replace(".", ",")}%`
                                          : ""}
                                        )
                                      </strong>
                                    </div>
                                  </>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </MDBox>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton color="info" onClick={() => setVendasComparativoOpen(false)}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={vendasDetalheOpen}
        onClose={() => setVendasDetalheOpen(false)}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
        PaperComponent={isMobile ? undefined : PaperComponent}
        aria-labelledby="draggable-vendas-detalhe-title"
        PaperProps={{
          sx: {
            width: { sm: "95vw" },
            maxWidth: { sm: "95vw" },
          },
        }}
      >
        <DialogTitle
          id="draggable-vendas-detalhe-title"
          sx={{ cursor: { xs: "default", sm: "move" } }}
        >
          Detalhe de vendas — {getMesLabel(vendasDetalheMes)} {vendasDetalheAno}
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            px: { xs: 1, sm: 3 },
            py: { xs: 1.5, sm: 2 },
          }}
        >
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            flexWrap="wrap"
            gap={2}
            mb={2}
          >
            <MDBox display="flex" flexWrap="wrap" gap={3}>
              <MDTypography variant="button" color="text">
                Total do gráfico: <strong>{fmt.format(vendasDetalheTotalGrafico)} €</strong>
              </MDTypography>

              <MDTypography variant="button" color="text">
                Total das linhas carregadas:{" "}
                <strong>{fmt.format(vendasDetalheTotalLinhas)} €</strong>
              </MDTypography>
            </MDBox>

            <MDBox
              display="flex"
              alignItems="center"
              gap={1}
              sx={{
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <MDTypography variant="button" color="text">
                Mostrar:
              </MDTypography>

              <select
                value={vendasDetalheLimite}
                onChange={(e) => recarregarDetalheVendasMes(Number(e.target.value))}
                disabled={vendasDetalheLoading}
                style={{
                  minWidth: 110,
                  padding: "6px 8px",
                  border: "1px solid rgba(0,0,0,0.2)",
                  borderRadius: 6,
                  fontSize: 13,
                  background: "#fff",
                }}
              >
                <option value={100}>100</option>
                <option value={250}>250</option>
                <option value={500}>500</option>
                <option value={0}>Todas</option>
              </select>
            </MDBox>
          </MDBox>
          <MemoizedProjectTable
            tableKey="dashboard-vendas-detalhe-mes"
            version="v2"
            columns={vendasDetalheColumns}
            data={vendasDetalheRows}
            loading={vendasDetalheLoading}
            emptyMessage="Sem faturas para apresentar."
            minWidth={1200}
            maxHeight="60vh"
            rowActions={vendasDetalheRowActions}
            actionsPosition="left"
            showKeepFilters
          />
        </DialogContent>

        <DialogActions>
          <MDButton color="info" onClick={() => setVendasDetalheOpen(false)}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={faturaDetalheOpen}
        onClose={() => setFaturaDetalheOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperComponent={isMobile ? undefined : FaturaPaperComponent}
        aria-labelledby="fatura-detalhe-title"
        PaperProps={{
          sx: {
            width: { xs: "calc(100% - 16px)", md: "90vw" },
            maxWidth: "90vw",
          },
        }}
      >
        <DialogTitle id="fatura-detalhe-title" sx={{ cursor: { xs: "default", sm: "move" } }}>
          {String(faturaDetalheSelecionada?.faturanumero || "").startsWith("9")
            ? "Devolução"
            : "Fatura"}{" "}
          {faturaDetalheSelecionada?.faturanumero || ""}
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            px: { xs: 1, sm: 3 },
            py: { xs: 1.5, sm: 2 },
          }}
        >
          <MDBox display="flex" flexWrap="wrap" gap={3} mb={2}>
            <MDTypography variant="button" color="text">
              Cliente: <strong>{faturaDetalheSelecionada?.cliente || ""}</strong>
            </MDTypography>

            <MDTypography variant="button" color="text">
              Vendedor: <strong>{faturaDetalheSelecionada?.vendedor || ""}</strong>
            </MDTypography>

            <MDTypography variant="button" color="text">
              Total da fatura:{" "}
              <strong>{fmt.format(num(faturaDetalheSelecionada?.valorfatura))} €</strong>
            </MDTypography>

            <MDTypography variant="button" color="text">
              Total das linhas: <strong>{fmt.format(faturaDetalheTotalLinhas)} €</strong>
            </MDTypography>
          </MDBox>

          <ProjectTable
            tableKey="dashboard-vendas-detalhe-fatura"
            version="v1"
            columns={faturaDetalheColumns}
            data={faturaDetalheRows}
            loading={faturaDetalheLoading}
            emptyMessage="Sem linhas para apresentar."
            minWidth={950}
            maxHeight="55vh"
            defaultSortBy={[
              {
                id: "item",
                desc: false,
              },
            ]}
            showKeepFilters
          />
        </DialogContent>

        <DialogActions>
          <MDButton color="info" onClick={() => setFaturaDetalheOpen(false)}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={topDialogOpen}
        onClose={() => setTopDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle id="draggable-dialog-title" sx={{ cursor: "move" }}>
          {topDialogTitle}
        </DialogTitle>

        <DialogContent dividers>
          {topDialogLoading ? (
            <MDBox py={3} display="flex" justifyContent="center">
              <CircularProgress size={24} />
            </MDBox>
          ) : (
            <MDBox sx={{ maxHeight: "60vh", overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {topDialogHeaders.map((h) => (
                      <th
                        key={h}
                        style={{
                          position: "sticky",
                          top: 0,
                          background: "#fff",
                          textAlign: "left",
                          padding: "8px 10px",
                          fontSize: 12,
                          color: "#7b809a",
                          borderBottom: "1px solid #eee",
                          zIndex: 1,
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topDialogRows.map((r, idx) => (
                    <tr key={`${r?.ref ?? "x"}-${idx}`}>
                      <td
                        style={{
                          padding: "8px 10px",
                          borderTop: "1px solid #f0f0f0",
                          fontSize: 12,
                        }}
                      >
                        {idx + 1}
                      </td>
                      <td
                        style={{
                          padding: "8px 10px",
                          borderTop: "1px solid #f0f0f0",
                          fontSize: 12,
                        }}
                      >
                        {r?.nome ? `${r.nome} (${r.ref})` : r?.ref ?? ""}
                      </td>
                      <td
                        style={{
                          padding: "8px 10px",
                          borderTop: "1px solid #f0f0f0",
                          fontSize: 12,
                        }}
                      >
                        {fmt.format(num(r?.total_atual))}
                      </td>
                      <td
                        style={{
                          padding: "8px 10px",
                          borderTop: "1px solid #f0f0f0",
                          fontSize: 12,
                        }}
                      >
                        {fmt.format(num(r?.total_anterior))}
                      </td>
                      <td
                        style={{
                          padding: "8px 10px",
                          borderTop: "1px solid #f0f0f0",
                          fontSize: 12,
                        }}
                      >
                        {fmt.format(num(r?.dif))}
                      </td>
                      <td
                        style={{
                          padding: "8px 10px",
                          borderTop: "1px solid #f0f0f0",
                          fontSize: 12,
                        }}
                      >
                        {r?.dif_pct == null ? "—" : `${fmt.format(num(r?.dif_pct))}%`}
                      </td>
                    </tr>
                  ))}

                  {!topDialogRows?.length && (
                    <tr>
                      <td
                        colSpan={6}
                        style={{ padding: "12px 10px", fontSize: 12, color: "#7b809a" }}
                      >
                        Sem dados para apresentar.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </MDBox>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton color="info" onClick={() => setTopDialogOpen(false)}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
